import executeContext from "../store/utils/quote-view/execute-context";
import { flattenQuoteData } from "../store/utils/flatten-quote-data";
import { queryClient } from "../units/react-query-client";
import { wrapRequest } from "../utils/single-promise-cache";
import { getApiUrl } from "../utils/get-api-url";

/**
 *
 * @param {*} path
 * @param {*} param1
 * @returns any
 */
const fetchQuotesApi = async (
  path,
  { headers, params, contentTypeHeader = "application/json", ...options } = {}
) => {
  const searchParams = new URLSearchParams({ ...params }).toString();
  const apiBase = getApiUrl("quotesapi");

  // slash (/) after apiBase is left out on purpose because ${path} includes a slash at the start of it
  const endpointUrl = `${apiBase}${path}${
    searchParams ? "?" + searchParams.toString() : ""
  }`;

  const response = await fetch(endpointUrl, {
    headers: {
      ...headers,
      Accept: "application/json",
      "Content-Type": contentTypeHeader,
      "Cache-Control": "no-cache"
    },
    credentials: "include",
    ...options
  });
  const { headers: resHeaders, status, ok } = response;
  const contentType = resHeaders.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    throw new Error("Network request failed. Status: " + status);
  }
  const result = await response.json();

  if (!ok) throw new Error(result.message || "Quotes API request failed");

  return result;
};

const quotes = async ({
  id,
  data,
  method = "GET",
  headers = {},
  contentTypeHeader
}) => {
  const endpoint = id ? `/quotes/${id}` : `/quotes`;

  const opts = {
    method
  };

  opts.headers = headers;

  if (contentTypeHeader) {
    opts.contentTypeHeader = contentTypeHeader;
  }

  if (method === "POST" || method === "PUT" || method === "PATCH") {
    if (contentTypeHeader === "application/x-www-form-urlencoded") {
      opts.body = Object.keys(data)
        .map(key => {
          return encodeURIComponent(key) + "=" + encodeURIComponent(data[key]);
        })
        .join("&");
    } else {
      opts.body = JSON.stringify(data);
    }
  }
  const quote = await fetchQuotesApi(endpoint, opts);

  if (method === "POST" || method === "PUT") {
    // POST, PUT return the full quote. Cache that for any subsequent GET calls
    // GET calls are already cached by react-query (useQuery) or the getQuoteCached function
    queryClient.setQueryData(["quotes.getQuote", { id: quote.quoteId }], quote);
  } else if (method === "PATCH") {
    // PATCH currently doesn't run client-side rulesets (PPQ-8115)
    // Invalidate the queries so that the next GET will update the quote.
    // `quote.quoteId` may not be part of the PATCH response so use `id` instead.

    queryClient.removeQueries(["quotes.getQuote", { id }]);
  }
  return quote;
};

// the only usage of this endpoint, at the moment, is sending a PATCH from quote home
// this is why we set the default value for `method` to PATCH.
const quoteHome = async ({
  id,
  data,
  method = "PATCH",
  headers = {},
  contentTypeHeader
}) => {
  const endpoint = `/quotehome/${id}`;
  const opts = {
    method
  };
  opts.headers = headers;
  opts.contentTypeHeader = contentTypeHeader;
  opts.body = Object.keys(data)
    .map(key => {
      return encodeURIComponent(key) + "=" + encodeURIComponent(data[key]);
    })
    .join("&");
  const quote = await fetchQuotesApi(endpoint, opts);
  queryClient.setQueryData(["quotes.getQuote", { id: quote.quoteId }], quote);

  return quote;
};

export const getQuote = quotes;

// This is using react query cache to be used outside of react-query
// Do not use this with a useQuery hook
export const getQuoteCached = async args => {
  const queryKey = ["quotes.getQuote", { id: args.id }];
  const cachedData = queryClient.getQueryData(queryKey);
  if (cachedData) {
    return cachedData;
  }
  const data = await quotes(args);
  queryClient.setQueryData(queryKey, data);
  return data;
};

export const postQuote = (using = {}) => quotes({ method: "POST", ...using });
export const putQuote = (using = {}) => quotes({ method: "PUT", ...using });
export const patchQuote = (using = {}) => quotes({ method: "PATCH", ...using });

export const getPriceTable = ({ id, params = {} }) =>
  fetchQuotesApi(`/quotes/${id}/price-list`, { params });
export const patchPriceTable = (using = {}) =>
  quotes({
    method: "PATCH",
    contentTypeHeader: "application/x-www-form-urlencoded",
    ...using
  });
export const patchQuoteHomePriceTable = (using = {}) =>
  quoteHome({
    method: "PATCH",
    contentTypeHeader: "application/x-www-form-urlencoded",
    ...using
  });
export const patchEffectiveDate = (using = {}) =>
  quotes({
    method: "PATCH",
    ...using
  });

export const getAlternateQuotes = ({ id }) =>
  fetchQuotesApi(`/quotes/${id}/alternative-quotes`);
export const getClaims = ({ policyId, quoteId }) =>
  fetchQuotesApi(`/claims/${policyId}`, { params: { quoteId } });
export const getForms = ({ productId }) =>
  fetchQuotesApi(`/products/${productId}/forms`);

export const getFastPremiumQuestions = ({ state }) =>
  fetchQuotesApi(`/fastpremium/interviewQuestions/${state}`);
export const getFastPremiumEstimates = payload =>
  fetchQuotesApi(`/fastpremium`, {
    method: "POST",
    body: JSON.stringify(payload)
  });

export const getProducts = data =>
  fetchQuotesApi(`/products`, { params: data });
export const getProduct = ({ id, ...data }) =>
  getProducts(
    id
      ? {
          ...data,
          quoteId: id
        }
      : data
  ).then(([product = {}]) => product);

export const getContextWithWrapper = wrapRequest(({ id }) =>
  fetchQuotesApi(`/quotes/${id}/layout`)
);
// this is identical to getContextWithWrapper (which was getContext before) but does not use single-promise-cache that was needed
// before we started using react query
export const getContext = ({ id }) => fetchQuotesApi(`/quotes/${id}/layout`);

export const getIdentifiers = ({ id }) =>
  fetchQuotesApi(`/quotes/${id}/identifiers`);
export const getSearchResults = data =>
  fetchQuotesApi(`/quotes`, { params: { resultsetType: "compact", ...data } });

export const getSuggestResults = data =>
  fetchQuotesApi(`/quotes`, {
    params: {
      suggest: true,
      ...data
    }
  });

export const getInspectionStatus = ({ id }) =>
  fetchQuotesApi(`/quotes/${id}/inspection-status`);

export const getResources = () => fetchQuotesApi("/resources");

export const getProducerDefaults = () => fetchQuotesApi(`/producer-defaults`);
export const postProducerDefaults = ({ data }) =>
  fetchQuotesApi(`/producer-defaults`, {
    method: "POST",
    body: JSON.stringify(data)
  });

export const postFnolToClaims = data =>
  fetchQuotesApi(`/claims/channel`, {
    method: "POST",
    body: JSON.stringify(data)
  });

export const loadQuote = async quoteNumber => {
  const [quote, context, product] = await Promise.all([
    getQuote({ id: quoteNumber }),
    getContextWithWrapper({ id: quoteNumber }),
    getProduct({ id: quoteNumber }).catch(error => {
      console.error(error);
      return {};
    })
  ]);

  return {
    ...quote,
    product,
    documents: quote?.carriers?.[0]?.documents || [],
    insuredName: quote.inputs.InsuredName,
    sections: executeContext(context, flattenQuoteData(quote))
  };
};
